import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common';
import { CreateMaterialRequest, UpdateMaterialRequest, Material, MaterialStatus } from '@/types/material';
import { categoryApi } from '@/api/endpoints/categories';
import { FormattedNumberInput } from '@/utils/number-format';
import { toast } from '@/store/toastStore';
import { QUANTITY_DECIMAL_PLACES } from '@/constants/number';

interface MaterialFormProps {
  material?: Material;
  onSubmit: (data: CreateMaterialRequest | UpdateMaterialRequest) => void;
  onCancel: () => void;
  onSaveAndClose?: (data: CreateMaterialRequest | UpdateMaterialRequest) => void;
  loading?: boolean;
}

const MaterialForm: React.FC<MaterialFormProps> = ({
  material,
  onSubmit,
  onCancel,
  onSaveAndClose,
  loading = false,
}) => {
  const { t } = useTranslation();

  const isEditing = !!material;

  const [formData, setFormData] = useState<CreateMaterialRequest>({
    name: '',
    categoryId: '',
    categoryGroupId: '',
    unit: '',
    standardPrice: 0,
    actualQuantity: 0,
    availableQuantity: 0,
    minimumStock: 0,
    notes: '',
    status: 'active',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availableGroups, setAvailableGroups] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [categoryHierarchy, setCategoryHierarchy] = useState<any[]>([]);

  // Initialize form data when material prop changes
  useEffect(() => {
    if (material) {
      setFormData({
        name: material.name,
        categoryId: material.categoryId,
        categoryGroupId: material.categoryGroupId || '',
        unit: material.unit,
        standardPrice: material.standardPrice,
        actualQuantity: material.actualQuantity,
        availableQuantity: material.availableQuantity,
        minimumStock: material.minimumStock,
        notes: material.notes || '',
        status: material.status,
      });
    }
  }, [material]);

  // Load required data from APIs
  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      try {
        // Load categories hierarchy and derive parent categories (material_category)
        const hierarchy = await categoryApi.getCategoryHierarchy('active');
        setCategoryHierarchy(hierarchy);
        const parentCategories = Array.isArray(hierarchy)
          ? hierarchy.filter((cat: any) => cat.type === 'material_category')
          : [];
        setCategories(parentCategories);



        // Load material groups - for now use empty array since groups API isn't implemented
        // TODO: Implement material groups API and load real data
        // Note: Do not clear availableGroups here to avoid overwriting groups derived from hierarchy
      } catch (error) {
        // Show error but don't block the form
        toast.error(
          t('common.error'),
          t('materials.form.loadDataError')
        );
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, []);

  // Update available groups when category changes
  useEffect(() => {
    // If we have a categoryId but hierarchy is not loaded yet, wait
    if (formData.categoryId && (!Array.isArray(categoryHierarchy) || categoryHierarchy.length === 0)) {
      return;
    }
    
    if (formData.categoryId) {
      // Find selected parent in hierarchy and list its direct child categories (any type)
      const selectedParent = categoryHierarchy.find(
        (cat: any) => cat.code === formData.categoryId
      );

      const groups = Array.isArray(selectedParent?.children)
        ? selectedParent.children
        : [];
      setAvailableGroups(groups);

      // Only reset group if the current group is not in the newly available groups
      const currentGroupCode = formData.categoryGroupId;
      const hasCurrentGroup = groups.some((g: any) => g.code === currentGroupCode);
      if (!hasCurrentGroup) {
        setFormData(prev => ({ ...prev, categoryGroupId: '' }));
      }
    } else {
      setAvailableGroups([]);
      setFormData(prev => ({ ...prev, categoryGroupId: '' }));
    }
  }, [formData.categoryId, categoryHierarchy]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('materials.form.validation.nameRequired');
    }
    if (!formData.categoryId) {
      newErrors.categoryId = t('materials.form.validation.categoryRequired');
    }
    if (!formData.unit.trim()) {
      newErrors.unit = t('materials.form.validation.unitRequired');
    }
    // Allow standardPrice = 0; only negative values are invalid
    if (formData.standardPrice < 0) {
      newErrors.standardPrice = t('materials.form.validation.standardPricePositive');
    }
    if (formData.actualQuantity < 0) {
      newErrors.actualQuantity = t('materials.form.validation.actualQuantityNonNegative');
    }
    if ((formData.availableQuantity || 0) < 0) {
      newErrors.availableQuantity = t('materials.form.validation.availableQuantityNonNegative');
    }
    if (formData.minimumStock < 0) {
      newErrors.minimumStock = t('materials.form.validation.minimumStockNonNegative');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (isEditing) {
      onSubmit({ ...formData, code: material!.code } as UpdateMaterialRequest);
    } else {
      onSubmit(formData);
    }
  };

  const handleSaveAndClose = () => {
    if (!validateForm()) {
      return;
    }

    if (onSaveAndClose) {
      if (isEditing) {
        onSaveAndClose({ ...formData, code: material!.code } as UpdateMaterialRequest);
      } else {
        onSaveAndClose(formData);
      }
    }
  };

  const handleInputChange = (field: keyof CreateMaterialRequest, value: string | number | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const statusOptions: { value: MaterialStatus; label: string }[] = [
    { value: 'active', label: t('materials.form.status.active') },
    { value: 'inactive', label: t('materials.form.status.inactive') },
  ];

  // Generate display values for readonly fields
  const materialCode = material?.code || t('materials.form.autoGenerated');
  const createdBy = material?.createdBy || t('materials.form.currentUser');

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Top Row - Material Code (readonly) and Created By (readonly) as specified */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Material Code - top-left as specified */}
        <div>
          <label htmlFor="materialCode" className="block text-sm font-medium text-gray-700 mb-1">
            {t('materials.form.fields.materialCode')}
          </label>
          <input
            type="text"
            id="materialCode"
            value={materialCode}
            readOnly
            className="input-field bg-gray-50 text-gray-500 cursor-not-allowed"
            placeholder={t('materials.form.fields.materialCode')}
          />
        </div>

        {/* Created By - top-right, next to the code as specified */}
        <div>
          <label htmlFor="createdBy" className="block text-sm font-medium text-gray-700 mb-1">
            {t('materials.form.fields.createdBy')}
          </label>
          <input
            type="text"
            id="createdBy"
            value={createdBy}
            readOnly
            className="input-field bg-gray-50 text-gray-500 cursor-not-allowed"
            placeholder={t('materials.form.fields.createdBy')}
          />
        </div>
      </div>

      {/* Material Name - full-width field below the top row as specified */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          {t('materials.form.fields.materialName')} *
        </label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className={`input-field ${errors.name ? 'border-red-500' : ''}`}
          placeholder={t('materials.form.placeholders.materialName')}
          disabled={loading}
        />
        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
      </div>

      {/* Category + Group (if wood) - placed in two columns as specified */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Category */}
        <div>
          <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700 mb-1">
            {t('materials.form.fields.category')} *
          </label>
          <select
            id="categoryId"
            value={formData.categoryId}
            onChange={(e) => handleInputChange('categoryId', e.target.value)}
            className={`input-field ${errors.categoryId ? 'border-red-500' : ''}`}
            disabled={loading || loadingData}
          >
            <option value="">
              {loadingData ? t('materials.form.loading') : t('materials.form.placeholders.selectCategory')}
            </option>
            {categories.map((category) => (
              <option key={category.id} value={category.code}>
                {category.name}
              </option>
            ))}
          </select>
          {errors.categoryId && <p className="text-red-500 text-xs mt-1">{errors.categoryId}</p>}
        </div>

        {/* Group (conditional) */}
        <div>
          <label htmlFor="categoryGroupId" className="block text-sm font-medium text-gray-700 mb-1">
            {t('materials.form.fields.categoryGroup')}{availableGroups.length > 0 && ` (${t('materials.form.ifApplicable')})`}
          </label>
          <select
            id="categoryGroupId"
            value={formData.categoryGroupId}
            onChange={(e) => handleInputChange('categoryGroupId', e.target.value)}
            className="input-field"
            disabled={loading || availableGroups.length === 0}
          >
            <option value="">{t('materials.form.placeholders.selectGroup')}</option>
            {availableGroups.map((group) => (
              <option key={group.id} value={group.code}>
                {group.name}
              </option>
            ))}
          </select>
          {availableGroups.length === 0 && formData.categoryId && (
            <p className="text-gray-500 text-xs mt-1">{t('materials.form.noGroupsInCategory')}</p>
          )}
        </div>
      </div>

      {/* Unit - full-width field */}
      <div>
        <label htmlFor="unit" className="block text-sm font-medium text-gray-700 mb-1">
          {t('materials.form.fields.unit')} *
        </label>
        <input
          type="text"
          id="unit"
          value={formData.unit}
          onChange={(e) => handleInputChange('unit', e.target.value)}
          className={`input-field ${errors.unit ? 'border-red-500' : ''}`}
          placeholder={t('materials.form.placeholders.unit')}
          disabled={loading}
        />
        {errors.unit && <p className="text-red-500 text-xs mt-1">{errors.unit}</p>}
      </div>

      {/* Standard Price + Actual Quantity + Available Quantity + Minimum Stock - next row, arranged left to right as specified */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Standard Price */}
        <div>
          <label htmlFor="standardPrice" className="block text-sm font-medium text-gray-700 mb-1">
            {t('materials.form.fields.standardPrice')} *
          </label>
          <FormattedNumberInput
            id="standardPrice"
            value={formData.standardPrice}
            onChange={(value) => handleInputChange('standardPrice', value)}
            className={`input-field ${errors.standardPrice ? 'border-red-500' : ''}`}
            placeholder="0"
            disabled={loading}
          />
          {errors.standardPrice && <p className="text-red-500 text-xs mt-1">{errors.standardPrice}</p>}
        </div>

        {/* Actual Quantity */}
        <div>
          <label htmlFor="actualQuantity" className="block text-sm font-medium text-gray-700 mb-1">
            {t('materials.form.fields.actualQuantity')}
          </label>
          <FormattedNumberInput
            id="actualQuantity"
            value={formData.actualQuantity}
            onChange={(value) => handleInputChange('actualQuantity', value)}
            className={`input-field ${errors.actualQuantity ? 'border-red-500' : ''}`}
            placeholder="0"
            maxDecimalPlaces={QUANTITY_DECIMAL_PLACES}
            disabled={loading}
          />
          {errors.actualQuantity && <p className="text-red-500 text-xs mt-1">{errors.actualQuantity}</p>}
        </div>

        {/* Available Quantity */}
        <div>
          <label htmlFor="availableQuantity" className="block text-sm font-medium text-gray-700 mb-1">
            {t('materials.form.fields.availableQuantity')}
          </label>
          <FormattedNumberInput
            id="availableQuantity"
            value={formData.availableQuantity || 0}
            onChange={(value) => handleInputChange('availableQuantity', value)}
            className={`input-field ${errors.availableQuantity ? 'border-red-500' : ''}`}
            placeholder="0"
            disabled={loading}
          />
          {errors.availableQuantity && <p className="text-red-500 text-xs mt-1">{errors.availableQuantity}</p>}
        </div>

        {/* Minimum Stock */}
        <div>
          <label htmlFor="minimumStock" className="block text-sm font-medium text-gray-700 mb-1">
            {t('materials.form.fields.minimumStock')}
          </label>
          <FormattedNumberInput
            id="minimumStock"
            value={formData.minimumStock}
            onChange={(value) => handleInputChange('minimumStock', value)}
            className={`input-field ${errors.minimumStock ? 'border-red-500' : ''}`}
            placeholder="0"
            decimalPlaces={QUANTITY_DECIMAL_PLACES}
            disabled={loading}
          />
          {errors.minimumStock && <p className="text-red-500 text-xs mt-1">{errors.minimumStock}</p>}
        </div>
      </div>

      {/* Status */}
      <div>
        <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
          {t('materials.form.fields.status')} *
        </label>
        <select
          id="status"
          value={formData.status}
          onChange={(e) => handleInputChange('status', e.target.value as MaterialStatus)}
          className="input-field"
          disabled={loading}
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Notes (textarea) - bottom, spans full modal width as specified */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
          {t('materials.form.fields.notes')}
        </label>
        <textarea
          id="notes"
          rows={4}
          value={formData.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          className="input-field resize-none"
          placeholder={t('materials.form.placeholders.notes')}
          disabled={loading}
        />
      </div>

      {/* Action Buttons - Save and Save & Close positioned bottom-right as specified */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          {t('materials.form.buttons.cancel')}
        </Button>

        {/* Save Button */}
        <Button
          type="submit"
          variant="outline"
          loading={loading}
        >
          {isEditing ? t('materials.form.buttons.update') : t('materials.form.buttons.save')}
        </Button>

        {/* Save and Close Button - positioned to the right of Save button as specified */}
        {onSaveAndClose && (
          <Button
            type="button"
            variant="primary"
            onClick={handleSaveAndClose}
            loading={loading}
          >
            {isEditing ? t('materials.form.buttons.updateAndClose') : t('materials.form.buttons.saveAndClose')}
          </Button>
        )}
      </div>
    </form>
  );
};

export default MaterialForm;
